import React from 'react';
import { AppBar, Box, Button, Container, Toolbar, Typography, useMediaQuery, useTheme } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RestorePageIcon from '@mui/icons-material/RestorePage';
import { useTranslation } from 'react-i18next';
import CurrencySelector from '../shared/CurrencySelector';
import Logo from '../Logo';

const LandingHeader = ({ offer, onExportPDF, onUseAsTemplate, useAsTemplateDisabled = false, templateLoading = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: 'white',
        borderBottom: '1px solid rgba(187,203,184,0.12)',
        color: 'text.primary',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: '48px', sm: '64px' },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Logo + Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            <Logo height={isMobile ? 28 : 32} />
            {!isMobile && (
              <Typography sx={{ fontFamily: "'Manrope', sans-serif", fontWeight: 900, fontSize: '1.25rem', color: '#191c1d', letterSpacing: '-0.5px' }}>
                Ringostat
              </Typography>
            )}
          </Box>

          {/* Right side */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            <Box sx={{
              '& .MuiTypography-root': { fontSize: '0.8125rem', fontFamily: "'Inter', sans-serif", ...(isMobile && { display: 'none' }) },
              '& .MuiToggleButtonGroup-root': {
                bgcolor: '#e7e8e9',
                borderRadius: 99,
                p: '3px',
              },
              '& .MuiToggleButton-root': {
                fontFamily: "'Inter', sans-serif",
                fontSize: '0.75rem',
                fontWeight: 600,
                border: 'none !important',
                borderRadius: '99px !important',
                px: { xs: 1.5, sm: 2 },
                py: { xs: 0.25, sm: 0.5 },
                color: '#3c4a3c',
                '&.Mui-selected': {
                  bgcolor: '#00c853',
                  color: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                  '&:hover': { bgcolor: '#00a040' },
                },
                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
              },
            }}>
              <CurrencySelector offer={offer} />
            </Box>

            {onUseAsTemplate && (
              <Button
                onClick={onUseAsTemplate}
                disabled={useAsTemplateDisabled || templateLoading}
                variant="outlined"
                aria-label={t('history.useAsTemplate', 'Use as template')}
                startIcon={!isMobile ? <RestorePageIcon sx={{ fontSize: '18px !important' }} /> : undefined}
                sx={{
                  color: '#006e2a',
                  borderColor: 'rgba(0, 110, 42, 0.35)',
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  borderRadius: 1,
                  px: isMobile ? 1 : 2,
                  py: 0.75,
                  minWidth: isMobile ? 40 : undefined,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: '#00c853',
                    bgcolor: 'rgba(0, 200, 83, 0.06)',
                  },
                }}
              >
                {isMobile ? <RestorePageIcon sx={{ fontSize: 20 }} /> : t('history.useAsTemplate', 'Use as template')}
              </Button>
            )}

            {onExportPDF && (
              <Button
                onClick={onExportPDF}
                variant="contained"
                startIcon={!isMobile ? <PictureAsPdfIcon sx={{ fontSize: '18px !important' }} /> : undefined}
                sx={{
                  bgcolor: '#00c853',
                  color: 'white',
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  borderRadius: 1,
                  px: isMobile ? 1 : 2.5,
                  py: 0.75,
                  minWidth: isMobile ? 40 : undefined,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#00a040', boxShadow: 'none' },
                }}
              >
                {isMobile ? <PictureAsPdfIcon sx={{ fontSize: 20 }} /> : t('offers.exportToPDF', 'Export to PDF')}
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default LandingHeader;
